import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Folder } from 'lucide-react';
import { useState } from 'react';
import { backend } from '@/api/backend';
import { explainLine } from '@/api/explain';
import { ApiError, newIdempotencyKey } from '@/api/http';
import { Skeleton, Stamp } from '@/components/ui';
import { formatRelative } from '@/lib/format';
import { overlaps, registerBody, scopeLabel, whyNotRegistrable } from '@/pages/registration';

const retry = (n: number, e: unknown) => (e instanceof ApiError ? e.retryable && n < 2 : n < 2);

/**
 * A bucket's folders, each registrable as its own scope.
 *
 * This is where choosing a folder belongs: beside the bucket, in the listing
 * an operator is already reading, rather than behind a form that would make
 * them retype a name the page is already showing them.
 *
 * The listing behind it is delimited rather than recursive, so these are top
 * level only and no object key is observed. A folder deeper than the top level
 * is reachable two ways: register this folder and promote from its own filing
 * once it has been walked, or type the whole path into the scope form.
 */
export function BucketFolders({
  bucketName,
  companyId,
  accountId,
  region,
  existing = [],
}: {
  bucketName: string;
  companyId: string;
  accountId: string | null;
  region: string | null;
  /** Scopes already registered, so an overlap is said before it is created. */
  existing?: readonly { name: string; prefix: string }[];
}) {
  const q = useQuery({
    queryKey: ['bucket-folders', bucketName, companyId],
    queryFn: ({ signal }) => backend.listObservedBucketFolders(bucketName, companyId, signal),
    retry,
    staleTime: 60_000,
  });

  if (q.isPending) return <Skeleton className="mx-4 my-2 h-4" />;

  if (q.isError) {
    return (
      <p role="alert" className="px-4 py-2 pl-10 text-caption text-warn">
        {explainLine(q.error, `read the folders in ${bucketName}`)}
      </p>
    );
  }

  // Discovery observes folders on the same pass as buckets, so a bucket seen
  // but not yet swept has none. That is a wait, not a fault, and not an empty
  // bucket either.
  if (q.data.items.length === 0) {
    return (
      <p className="px-4 py-2 pl-10 text-caption text-ink-3">
        No folder has been observed here yet. Discovery records them on the same pass it lists the account; registering
        the whole bucket works meanwhile.
      </p>
    );
  }

  return (
    <div className="border-b border-line bg-panel-2/40">
      <p className="px-4 py-1.5 pl-10 text-caption text-ink-4">
        Top level only. Each is its own scope, with its own walk, package and handover.
      </p>
      {q.data.items.map((folder) => (
        <FolderRow
          key={folder.prefix}
          bucketName={bucketName}
          folder={folder}
          accountId={accountId}
          region={region}
          clashes={overlaps({ name: bucketName, prefix: folder.prefix }, existing)}
        />
      ))}
    </div>
  );
}

function FolderRow({
  bucketName,
  folder,
  accountId,
  region,
  clashes,
}: {
  bucketName: string;
  folder: { prefix: string; lastObservedAt: string; isPresent: boolean };
  accountId: string | null;
  region: string | null;
  /** Registered scopes this folder would sit inside, or that sit inside it. */
  clashes: readonly { name: string; prefix: string }[];
}) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const qc = useQueryClient();
  const navigate = useNavigate();

  const scope = { name: bucketName, accountId, region, prefix: folder.prefix };

  const register = async () => {
    const why = whyNotRegistrable(scope);
    if (why) {
      setNote(why);
      return;
    }
    setBusy(true);
    setNote(null);
    try {
      const r = await backend.registerBucket(registerBody(scope), newIdempotencyKey());
      void qc.invalidateQueries({ queryKey: ['live-buckets'] });
      void qc.invalidateQueries({ queryKey: ['account-buckets'] });
      navigate(`/buckets/${r.bucket.id}`);
    } catch (e) {
      setNote(e instanceof ApiError ? e.message : 'The API did not respond.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-3 border-t border-line px-4 py-1.5 pl-10 text-small">
      <Folder className="h-3.5 w-3.5 flex-none text-ink-4" aria-hidden="true" />
      <span className="mono min-w-0 flex-1 truncate text-ink-2">{scopeLabel(bucketName, folder.prefix)}</span>
      {/* Reported rather than removed: the observation that it was there is
          still true, and a folder that has gone is worth seeing go. */}
      {!folder.isPresent && <Stamp tone="mut">GONE FROM LISTING</Stamp>}
      {/* Registering a folder of a bucket that is already registered whole
          means those objects belong to two scopes, and BOTH package and deliver
          them: the same files handed over twice. The backend allows it
          deliberately - a tenant may want exactly that - so the only thing
          owed is that nobody creates one without being told. It was said in
          the by-name form and not here, which is the path someone who already
          registered the bucket actually takes. */}
      {clashes.length > 0 && (
        <span className="flex-none text-caption text-warn" title={`Also covered by ${clashes.map((c) => scopeLabel(c.name, c.prefix)).join(', ')}`}>
          also in {clashes.map((c) => scopeLabel(c.name, c.prefix)).join(', ')} · would ship twice
        </span>
      )}
      <span className="mono flex-none text-caption text-ink-4">seen {formatRelative(folder.lastObservedAt)}</span>
      {note && (
        <span role="alert" className="flex-none text-caption text-warn">
          {note}
        </span>
      )}
      <button
        type="button"
        onClick={() => void register()}
        disabled={busy}
        title={`Register ${scopeLabel(bucketName, folder.prefix)} as its own scope`}
        className="flex-none rounded border border-line bg-panel-2 px-2.5 py-0.5 text-caption text-acc-2 transition-colors duration-fast hover:bg-hov disabled:opacity-50"
      >
        {busy ? 'Registering…' : 'Register this folder'}
      </button>
    </div>
  );
}
